<template>
  <div class="invoices-table" :class="{ 'table-on-modal': componentOnModal }">
    <ChangeQuotationStatus v-if="quotationToChange" :quotation="quotationToChange" @close="quotationToChange = null" @status-change="onQuotationStatusChange" />

    <FilterBar 
      :cookieName="(componentOnModal) ? modalTempCookie : filterOptions" 
      :class="{ 'no-totals': !showTotals }"
      filterBarType="invoices" 
      :skip-cookies="modalTempCookie"
      @change="filtersChanged" 
      :mainFilters="['SearchFilter', 'DateRangeFilter']"
      :secondaryFilters="secondaryFilters" 
      ref="invoices-table-filterbar"
    >
      <template v-if="showTotals" v-slot:right-bar>
        <TotalAmountsInvoices :num-format="'euro'" :quotationAmounts="quotationsList" />
      </template>
    </FilterBar>


    <RecurrentInvoicesTable v-if="showRecurring && !quotationsList" />

    <div class="content--inner">
      <overlay-scrollbars :options="scrollBarsOptions" class="invoices-table-scrollbar">
        <b-table
          primary-key="id"
          :class="['gk-table gk-table--responsive gk-table--striped gk-table--hover', { 'gk-table--statusmark': !selectableRows }]"
          :items="invoiceList"
          :fields="fields"
          :tbody-tr-class="rowClass"
          :key="tableKey"
          @row-clicked="rowClicked"
          :busy="loading"
          show-empty
          table-busy
        >
          <template #empty="">
            <EmptyState :class="{ 'emptystate-simple': componentOnModal }">
              <template v-if="!componentOnModal" v-slot:image>
                <img src="/static/img/empty-states/empty-state-cost.png">
              </template>
              <template v-slot:title>
                <span v-if="quotationsList">
                  {{ $gettext('We couldn\'t find any quotations') }}
                </span>
                <span v-else>
                  {{ $gettext('We couldn\'t find any invoices') }}
                </span>
              </template>
              <template v-slot:content>
                {{ $gettext('Start by creating a new one or try updating your filters.') }}
              </template>
              <template v-if="!componentOnModal" v-slot:cta>
                <GekkoButton 
                  v-if="quotationsList"
                  :to="{ name: 'QuotationAdd' }"
                  :text="$gettext('Add a new quotation')"
                  icon="fa-solid fa-plus"
                />
                <GekkoButton
                  v-else 
                  :to="{ name: 'InvoiceAdd' }"
                  :text="$gettext('Add a new invoice')"
                  icon="fa-solid fa-plus"
                />
              </template>
            </EmptyState>
          </template>
          <template #table-busy="">
              <LoadingSpinner min-height />
          </template>

          <template v-if="selectableRows" #cell(selector)="row">
            <b-form-checkbox v-model="row.item.selected" @change="selectedTableItem" />
          </template>

          <template #cell(invoice_date)="data">
            <div class="row-status-indicator"></div>
            <span v-if="data.item.correction" class="align-dates">{{ data.item.correction.timeframe }}</span>
            <span v-else class="align-dates">{{ data.value | formatDate }}</span>
          </template>

          <template #cell(number)="data">
            <span v-if="data.item.credited_invoice" class="">{{ data.value }} {{ $gettext('credited') }}</span>
            <span v-else class="">
              <span v-if="data.item.correction" class="">{{ data.item.correction.name }}</span>
              <span v-else class="">{{ data.value }}</span>
            </span>
          </template>

          <template #cell(customer)="data">
            <span v-if="data.item.correction && data.item.correction.no_customer">---</span>
            <div v-else>
              <div class="d-block" v-if="data.item.recipient_customer"><strong>{{ data.item.recipient_customer.name }}</strong></div>
              <span v-if="data.item.project">{{ data.item.project.name }}</span>
            </div>
          </template>

          <template #cell(created_by)="data">
            <div class="d-flex justify-content-center"><Avatar :user-id="data.item.created_by.id"></Avatar></div>
          </template>

          <template #cell(currency_total_amount)="data">
            <div class="text-right">{{ data.value | currencyString(data.item.currency) }}</div>
            <span v-if="data.item.currency !== 'EUR' && data.item.status !== 'd'">
              <span v-if="data.item.total_amount === 0.0">
                <GekkoButton
                    :href="getUrl('invoices_add_payment', data.item.id)"
                    :id="'tooltip-zeroamount-' + data.item.id"
                    :text="'(' + (data.item.total_amount | $options.filters.currency('EUR', 2)) + ')'"
                    icon="fa-solid fa-triangle-exclamation"
                    variant="link text-danger"
                    class="inline"
                    v-b-tooltip.focus
                  />
                <b-tooltip :target="'tooltip-zeroamount-' + data.item.id" triggers="hover">
                  <div v-if="currentCompany.country === 'ES'" class="tooltip-text">
                    {{ $gettext('Set the total amount in EUR on the \'Add payment\' page to complete the invoice. The VAT report will only then take this into account.') }}
                  </div>
                  <div v-else class="tooltip-text">
                    {{ $gettext('Set the total amount in EUR on the \'Add payment\' page to complete the invoice.') }}
                  </div>
                </b-tooltip>
              </span>
              <span v-else>({{ data.item.total_amount | currencyString('EUR') }})</span>
            </span>
          </template>

          <template #cell(status)="data">
            <div class="status-cell">
              <StatusIcons :item="data.item" />
              <StatusBadge :display-status="data.item.display_status" />
            </div>
          </template>

          <template v-if="!componentOnModal" #cell(actions)="data">
            <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="link" right no-caret>
              <template #button-content>
                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
              </template>

              <b-dropdown-item v-if="!data.item.correction && !quotationsList" @click="downloadInvoice(data.item.id)">
                <font-awesome-icon icon="fa-solid fa-download" />
                <span v-translate>Download</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="quotationsList" @click="downloadQuotation(data.item.id)">
                <font-awesome-icon icon="fa-solid fa-download" />
                <span v-translate>Download</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="!data.item.correction && !quotationsList" :to="{ name: 'InvoiceSend', params: { invoiceId: data.item.id } }">
                <font-awesome-icon icon="fa-regular fa-envelope" />
                <span>{{ getSendText(data.item.status) }}</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="quotationsList && !['accepted', 'draft', 'invoiced'].includes(data.item.status)" :to="{ name: 'QuotationSend', params: { quotationId: data.item.id } }">
                <font-awesome-icon icon="fa-regular fa-envelope" />
                <span v-translate>Send reminder</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="quotationsList && data.item.status === 'draft'" :to="{ name: 'QuotationSend', params: { quotationId: data.item.id } }">
                <font-awesome-icon icon="fa-regular fa-envelope" />
                <span v-translate>Send quotation</span>
              </b-dropdown-item>
              
              <b-dropdown-item v-if="data.item.display_status.detailed_status === 'draft' && !quotationsList" :to="{ name: 'InvoiceEdit', params: { invoiceId: data.item.id } }">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                <span v-translate>Edit draft</span>
              </b-dropdown-item>
              <b-dropdown-item v-else-if="data.item.uploaded" :to="{ name: 'InvoiceEdit', params: { invoiceId: data.item.id } }">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                <span v-translate>Edit invoice</span>
              </b-dropdown-item>
              
              <b-dropdown-item v-if="quotationsList && !['accepted', 'invoiced'].includes(data.item.status)" :to="{ name: 'QuotationEdit', params: { quotationId: data.item.id } }">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                <span v-translate>Edit quotation</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="quotationsList" @click="changeQuotationStatus(data.item)">
                <font-awesome-icon icon="fa-solid fa-rotate-right" />
                <span v-translate>Change status</span>
              </b-dropdown-item>
              
              <b-dropdown-item v-if="data.item.correction" :to="{ name: 'InvoiceEdit', params: { invoiceId: data.item.id } }">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                <span v-translate>Edit correction</span>
              </b-dropdown-item>
              
              <b-dropdown-item v-if="!data.item.uploaded && !data.item.correction && !quotationsList" @click="setInvoiceToCopy(data.item)">
                <font-awesome-icon icon="fa-regular fa-copy" />
                <span v-translate>Copy invoice</span>
              </b-dropdown-item>

              <b-dropdown-item v-if="(quotationsList && data.item.status === 'accepted')" @click="toInvoice(data.item.id)">
                <font-awesome-icon icon="fa-regular fa-copy" />
                <span v-translate>Copy to invoice</span>
              </b-dropdown-item>

              <b-dropdown-item 
                v-if="!data.item.correction && creditableStatuses.includes(data.item.display_status.detailed_status) && !quotationsList"
                @click="creditInvoice(data.item.id)"
              >
                <font-awesome-icon icon="fa-solid fa-rotate-left" />
                <span v-translate>Credit invoice</span>
              </b-dropdown-item>
              
              <b-dropdown-item v-if="data.item.status != 'd' && !data.item.correction && !quotationsList" :to="{ name: 'InvoicePayments', params: { invoiceId: data.item.id } }">
                <font-awesome-icon icon="fa-regular fa-credit-card" />
                <span v-if="data.item.status === 'p'" v-translate>View payments</span>
                <span v-else v-translate>Add payment</span>
              </b-dropdown-item>
              <DropdownItemDelete 
                :item="data.item"
                :item-type="(quotationsList) ? 'quotation' : 'invoice'"
                @item-deleted="onInvoiceDelete"
              />
            </b-dropdown>
          </template>

          <template slot="bottom-row" v-if="nextUrl">
            <td :colspan="fields.length" class="table-load-more">
              <GekkoButton 
                v-if="nextUrl && !fetchingData" 
                @click="handleScroll()"
                id="load_more_button" 
                class="text-info"
                variant="link" 
                :text="$gettext('Load more invoices')" 
              />
              <LoadingSpinner v-if="fetchingData" />
            </td>
          </template>
        </b-table>
      </overlay-scrollbars>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex"

import { promptDownload } from "@/utils/"

import invoices from "@/mixins/invoices"

import InvoiceService from "@/store/services/invoice.service.js"
import QuotationService from "@/store/services/quotation.service.js"

import Avatar from "@/components/Avatar"
import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton"
import ChangeQuotationStatus from "@/components/invoices/ui/ChangeQuotationStatus"
import TotalAmountsInvoices from "@/components/invoices/ui/TotalAmountsInvoices"
import EmptyState from "@/components/ui/EmptyState"
import RecurrentInvoicesTable from "@/components/invoices/ui/RecurrentInvoicesTable"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DropdownItemDelete from "@/components/ui/DropdownItemDelete"
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import StatusIcons from "@/components/invoices/ui/StatusIcons"

// import InvoiceCopyModal from "@/components/invoices/ui/InvoiceCopyModal"

export default {
  name: "InvoicesTable",
  mixins: [invoices],
  components: { 
    GekkoButton, 
    Avatar, 
    FilterBar, 
    TotalAmountsInvoices, 
    EmptyState, 
    RecurrentInvoicesTable, 
    LoadingSpinner,
    ChangeQuotationStatus,
    DropdownItemDelete,
    StatusBadge,
    StatusIcons,
    // InvoiceCopyModal
  },
  props: {
    data_object: Object,
    overlayScrollb: {
      type: Boolean,
      default: false
    }, 
    showTotals: {
      type: Boolean,
      default: true
    },
    showRecurring: {
      type: Boolean,
      default: true
    },
    componentOnModal: {
      type: Boolean,
      default: false
    },
    modalTempCookie: {
      type: String,
      default: null
    },
    selectableRows: {
      type: Boolean,
      default: false
    },
    checkedRows: {
      type: Array,
      default: () => []
    },
    forceFilters: {
      type: Object,
      default: () => {}
    },
    quotationsList: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      filters: {},
      fields: [
        { key: 'invoice_date', label: this.$gettext('Date'), sortable: false, tdClass: 'col-date width-125' },
        { key: 'number', label: this.$gettext('Invoice number'), sortable: false, tdClass: '' },
        { key: 'customer', label: this.$gettext('Customer'), sortable: false, tdClass: 'col-customer' },
        { key: 'created_by', label: this.$gettext('User'), sortable: false, tdClass: 'col-avatar' },
        { key: 'currency_total_amount', label: this.$gettext('Amount'), sortable: false, tdClass: 'col-amount', thClass: 'text-right' },
        { key: 'status', label: this.$gettext('Status'), sortable: false, tdClass: 'col-status', thClass: 'text-center' },
      ],
      invoiceList: [],
      customerList: {},
      projectList: {},
      nextUrl: '',
      totalNumber: 0,
      fetchingData: false,
      tableKey: 0,
      loading: true,
      scrollBarsOptions: {
        callbacks : {
          onScrollStop: this.handleScroll
        }
      },
      quotationToChange: null,
    }
  },
  computed:{
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
      creditableStatuses: 'invoices/getCreditableStatuses'
    }),
    selectedTableItems () {
      return (this.invoiceList) ? this.invoiceList.filter(item => item.selected).map((item) => item.id) : null
    },
    secondaryFilters () {
      if (this.quotationsList)
        return ['QuotationsCustomerFilter', 'QuotationsStatusFilter']
      else
        return ['InvoicesCustomerFilter', 'InvoicesStatusFilter']
    },
    filterOptions () {
      if (this.quotationsList)
        return 'QuotationsFilterOptionsVue2'
      else
        return 'InvoicesFilterOptionsVue2'
    }
  },
  watch: {
    invoiceList (newValue) {
      this.$store.commit('invoices/SET_TEMP_INVOICE_LIST', newValue);
    },
    nextUrl (newValue) {
      this.$store.commit('invoices/SET_TEMP_INVOICE_LIST_NEXT', newValue);
    }
  },
  created () {
    if (this.selectableRows) {
      this.fields.unshift({ key: 'selector' })
    }
    if (!this.componentOnModal) {
      this.fields.push({ key: 'actions', label: this.$gettext('Actions'), sortable: false, tdClass: 'col-actions', thClass: 'text-center' })
    }
    window.addEventListener('scroll', this.handleScroll);
    this.$store.commit("filterStore/SET_OWNER_COMPANY", this.currentCompany.id)

    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'invoices/REFRESH_INVOICE_LIST') {
        this.refreshData()
      }
    })
  },
  destroyed () {
    this.unsubscribe()
    window.removeEventListener('scroll', this.handleScroll);
    if (this.componentOnModal) {
      this.$emit('destroyed')
    }
  },
  methods: {
    ...mapActions({ 
      setInvoiceToCopy: "invoices/setInvoiceToCopy",
    }),
    toInvoice(quotationId){
      QuotationService.toInvoice(quotationId).then((response) => {
        this.$router.push({ name: 'InvoiceView', params: { invoiceId: response.data } }).catch(() => {})
      })
    },
    getInvoices (filters) {
      this.loading = true
      this.fetchingData = true

      filters.sender_company = this.currentCompany.id
      filters.extended = true
      this.filters = filters
      this.filters = { ...this.filters, ...this.forceFilters }

      if (!this.quotationsList) {
        InvoiceService.list(this.filters).then((response) => {
          this._processInvoiceList(response.data);
        })
      } else {
        QuotationService.list(this.filters).then((response) => {
          this._processInvoiceList(response.data);
        });
      }
    },
    _processInvoiceList(invoiceList) {
        this.fetchingData = false
        this.invoiceList = invoiceList.results
        this.nextUrl = invoiceList.next
        this.totalNumber = invoiceList.count
        this.loading = false
        if (this.checkedRows) this.checkRows()
    },
    refreshData () {
      this.$refs['invoices-table-filterbar'].filtersChanged()
    },
    reloadFilterStore () {
      console.log('reloadFilterStore')
      this.$refs['invoices-table-filterbar'].reloadFilterStore()
    },
    rowClicked (item) {
      if (!this.componentOnModal) {
        this.openViewInvoice(item.id)
        // this.$store.commit('invoices/SET_INVOICE_TO_EDIT', item);
        // this.$router.push({name: 'EditInvoice', params: {invoiceId: item.id}});
      } else {
        if (this.selectableRows) {
          const invoice_index = this.invoiceList.findIndex(invoice => invoice.id === item.id)
          this.$set(this.invoiceList, invoice_index, { ...this.invoiceList[invoice_index], selected: !item.selected })
          this.selectedTableItem()
        } else {
          this.$emit('row-clicked', item)
        }
      }
    },
    rowClass (item) {
      return (item) ? 'invoices-table-row ' + item.display_status.detailed_status : ''
    },
    selectedTableItem () {
      this.$emit('selected-item', this.selectedTableItems)
    },
    onInvoiceDelete (item_id) {
      for (var i in this.invoiceList) {
        if (this.invoiceList[i].id === item_id) {
          this.invoiceList.splice(i, 1)
          break
        }
      }
    },
    filtersChanged (value) {
      this.getInvoices(value)
    },
    isInViewport (element_id) {
      var element = document.getElementById(element_id)
      var rect = element.getBoundingClientRect();
      var html = document.documentElement;
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      );
    },
    handleScroll () {
      if (!this.fetchingData && this.nextUrl) {
        const loadNewInvoices = this.isInViewport('load_more_button');
        if (loadNewInvoices) {
          this.fetchingData = true;
          InvoiceService.loadNextPage(this.nextUrl).then((response) => {
            this.fetchingData = false;
            this.nextUrl = response.data.next;
            this.invoiceList = this.invoiceList.concat(response.data.results);
            if (this.checkedRows) this.checkRows()
          })
        }
      }
    },
    checkRows () {
      for (var i = 0; i < this.invoiceList.length; i++) {
        if (this.checkedRows.includes(this.invoiceList[i].id)) {
          this.$set(this.invoiceList, i, { ...this.invoiceList[i], selected: true })
        }
      }
    },
    urlAddPayment (item_id) {
      return window.Urls['invoices_add_payment'](item_id)
    },
    getSendText (status) {
      let status_text = ''
      switch (status) {
        case 'd':
          status_text = this.$gettext('Send invoice')
          break;
        case 's':
          status_text = this.$gettext('Send reminder')
          break;
        case 'p':
          status_text = this.$gettext('Send again')
          break;
      }
      return status_text
    },
    redirectTo (url, item_id) {
      const target_url = this.getUrl(url, item_id)
      if (target_url) window.location.href = target_url
    },
    downloadInvoice (invoice_id) {
      InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    downloadQuotation (quotation_id) {
      QuotationService.getQuotationPDFBlob(quotation_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    openViewInvoice (invoice_id) {
      if (!this.quotationsList) {
        this.$router.push({ name: 'InvoiceView', params: { invoiceId: invoice_id } })
      } else {
        this.$router.push({ name: 'QuotationView', params: { quotationId: invoice_id } })
      }
    },
    changeQuotationStatus(quotation) {
      this.quotationToChange = quotation;
    },
    onQuotationStatusChange() {
      this.getInvoices(this.filters);
    }
  }
};
</script>
<style lang="scss">
  .invoices-table {
    .os-host.invoices-table-scrollbar {
      min-height: 360px;
    }
    &.table-on-modal {
      .os-host.invoices-table-scrollbar {
        height: 300px !important;
        max-height: 300px;
        .os-content { padding: 0 !important; }
      }
    }
  }

  // table.table-transition .flip-list-move {
  //   transition: transform 0.3s;
  // }

  .col-date        { width: 0%; white-space: nowrap; }
  .col-description { width: 25%; min-width: 150px; font-size: 14px; }
  .col-customer    { width: 35%; min-width: 170px; }
  .col-amount      { width: 125px; min-width: 125px; white-space: nowrap; font-size: 18px; }
  .col-status      { width: 220px; min-width: 220px; white-space: nowrap; }
  .col-avatar      { width: 0%; }
  .col-actions      { 
    .dropdown {
      .dropdown-toggle {
        &:hover {
          background: $colorDraft50;
        }
      }
    }
  }

  @media (min-width: '992px') {
    .col-description { width: 30%; min-width: 200px; }
    .col-status      { width: 220px; min-width: 220px; }
  }

  .col-customer {
    > * { line-height: 1; }
    font-size: 16px;
    span { font-size: 14px; color: #6b7280; }
  }

  .invoices-table-row {
    height: 52px;
    cursor: pointer;
    td { padding: 20px; }
  }

  .status-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
</style>
<style lang="scss" scoped>

</style>
